// src/contexts/OfflineContext.js
//
// OFFLINE LISTENING, the React side
//
// One shared source of truth for what is saved on this device. It has to be
// shared rather than a plain hook, because three separate places ask the same
// question at the same time: the action sheet ("is this one saved?"), the
// offline library page ("show me all of them"), and the player ("play the
// local copy"). Three copies of that state would disagree the moment somebody
// saved a track from the action sheet while the library page was open.
//
// Everything about storage lives in utils/offlineStore.js. This file is only
// about React: keeping the list warm, tracking progress, and turning the
// server's error codes into something a person can read.

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import {
  listOffline, saveTrackOffline, removeOffline, clearOffline,
  offlineUsage, renewLeases, primeSavedIds, leaseState, daysLeft,
} from '../utils/offlineStore';

const OfflineContext = createContext(null);

// The server's codes, turned into sentences. Same reasoning as
// downloadErrorMessage in utils/downloadTrack.js: without this, five callers
// each invent their own wording, or, far more likely, going by the six
// swallowed-error bugs already found in this codebase, show nothing at all
// and leave a button spinning forever.
// A release date somebody can actually read. "14 March" beats an ISO string,
// and the year only appears when it is not this one, because "14 March 2026"
// in 2026 is noise.
//
// THE DATE ONLY, AND THE TIME DELIBERATELY LEFT OFF.
//
// The first version showed the time and suppressed it at midnight, on the
// grounds that "on 14 March at 00:00" reads like a machine wrote it. Testing
// it from Johannesburg showed why that does not work: a release stored as
// 00:00Z renders as "at 02:00" here, so the suppression never fires and the
// buyer is told a time the artist never chose.
//
// The artist picks the date in a datetime-local field, which has no timezone
// in it, so what got stored is already ambiguous about which midnight was
// meant. Showing a precise time built on an ambiguous value is worse than
// showing no time at all, and the exact minute is not what somebody waiting
// for a record wants to know.
function unlockWording(iso) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function offlineErrorMessage(err) {
  switch (err?.message) {
    case 'paid_tier_required':
    case 'fan_pro_required':   // older code name, kept so a stale bundle still reads right
      return 'Offline listening comes with Fan Pro, Artist Pro or Artist Premium. Upgrade to keep music on your device.';
    case 'purchase_required':
      return 'Buy this track first, then you can keep it offline.';
    case 'not_released_yet': {
      // Two different people hit this. One has paid and is being told to wait
      // for something they already own; the other has not bought it at all.
      // Sending both the same sentence is what made this feel like the
      // platform had forgotten the buyer handed over money.
      const when = unlockWording(err?.details?.release_date);
      if (err?.details?.owned) {
        return when
          ? `You own this. It unlocks on ${when}, and it will save to your device then.`
          : 'You own this. It will save to your device on release day.';
      }
      return when
        ? `Out on ${when}. Pre-order it and it is yours the moment it lands.`
        : "This track hasn't been released yet.";
    }
    case 'track_unavailable':
      return 'The artist has taken this track down.';
    case 'offline_limit_reached':
      return "You've reached the offline limit. Remove a few tracks to make room.";
    case 'device_storage_full':
      return "Your device is out of space. Free some up, or remove a few saved tracks.";
    case 'audio_fetch_failed':
    case 'audio_empty':
      return 'The download stopped partway. Try again on a better connection.';
    case 'offline_unsupported':
      return "This browser can't store music offline. Try the installed app.";
    case 'not_authenticated':
      return 'Sign in to save music offline.';
    default:
      return "Couldn't save this for offline. Try again.";
  }
}

export function OfflineProvider({ children }) {
  const { user } = useAuth();

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage,   setUsage]   = useState({ count: 0, bytes: 0, quota: null });
  const [saving,  setSaving]  = useState({});   // trackId -> 0..1 or null
  const [error,   setError]   = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [supported, setSupported] = useState(true);

  const abortRefs = useRef({});   // trackId -> AbortController

  const refresh = useCallback(async () => {
    try {
      const list = await listOffline();
      setItems(list);
      // The whole meta record, not just the id. It carries the file_url the
      // copy was saved from, which is what lets playbackSrc notice that a
      // track has been re-uploaded since. See offlineCopyIsStale.
      primeSavedIds(list.filter(m => leaseState(m) !== 'expired'));
      setUsage(await offlineUsage());
      setSupported(true);
    } catch (err) {
      // A browser with IndexedDB disabled, or private mode on some versions of
      // Safari. Not an error to shout about, the feature simply isn't there.
      if (err?.message === 'offline_unsupported') setSupported(false);
      setItems([]);
      primeSavedIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Renew on reconnect, and once on load if we already have a network.
  //
  // This is the only moment entitlement can be re-checked, so it is also the
  // only moment a lease can be taken away, which is the right place for it:
  // the person is online and can be told why.
  // THE THROTTLE HAS TO OUTLIVE THE PAGE.
  //
  // This was a ref, so it reset on every full page load, and a full page load
  // is exactly when this runs. The once-an-hour rule therefore never applied
  // to the case it was written for: opening the app. Every single load started
  // the renewal sweep again from scratch, which is why get-offline-url was the
  // slowest request on every page I measured.
  //
  // localStorage survives the reload. Leases last 30 days and renewLeases now
  // only touches the ones inside their last week, so once every six hours is
  // already far more often than the lease needs.
  const RENEW_KEY = 'fm_offline_renewed_at';
  const RENEW_EVERY_MS = 6 * 3600000;
  const renewRef = useRef(0);

  const lastRenew = () => {
    if (renewRef.current) return renewRef.current;
    try { return Number(localStorage.getItem(RENEW_KEY)) || 0; } catch { return 0; }
  };
  const markRenewed = (t) => {
    renewRef.current = t;
    try { localStorage.setItem(RENEW_KEY, String(t)); } catch { /* private mode */ }
  };

  const renew = useCallback(async () => {
    if (!user) return;
    // This also fires on every online event, and phones on a flaky signal emit
    // that repeatedly.
    if (Date.now() - lastRenew() < RENEW_EVERY_MS) return;
    markRenewed(Date.now());
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const result = await renewLeases(session.access_token, user.id);
      if (result.renewed || result.revoked) await refresh();
    } catch { /* next reconnect will try again */ }
  }, [user, refresh]);

  useEffect(() => {
    if (!user) return;
    if (navigator.onLine) renew();
    const onOnline = () => renew();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user, renew]);

  // ── Saving ──────────────────────────────────────────────────────────────────

  const save = useCallback(async (track) => {
    if (!track?.id) return false;
    if (!user) { setError(offlineErrorMessage({ message: 'not_authenticated' })); return false; }
    if (saving[track.id] !== undefined) return false;   // already going

    setError('');
    setErrorCode('');
    setSaving(s => ({ ...s, [track.id]: 0 }));

    const controller = new AbortController();
    abortRefs.current[track.id] = controller;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      await saveTrackOffline(track, {
        authToken: session?.access_token,
        userId: user?.id || null,
        signal: controller.signal,
        onProgress: p => setSaving(s => (s[track.id] === undefined ? s : { ...s, [track.id]: p })),
      });
      await refresh();
      return true;
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(offlineErrorMessage(err));
        // The raw code as well as the sentence. offlineErrorMessage turns
        // purchase_required into prose, and prose is not something a button
        // can branch on, which is why a track priced through its album used
        // to dead end here: the save was refused and nothing offered a way to
        // buy it. OfflineSaveButton reads this to put that route back.
        setErrorCode(err?.message || '');
        console.warn('[offline] save failed', track.id, err?.message || err);
      }
      return false;
    } finally {
      delete abortRefs.current[track.id];
      setSaving(s => { const next = { ...s }; delete next[track.id]; return next; });
    }
  }, [user, saving, refresh]);

  const cancelSave = useCallback((trackId) => {
    abortRefs.current[trackId]?.abort();
  }, []);

  // ── Removing ────────────────────────────────────────────────────────────────

  const remove = useCallback(async (trackId) => {
    try {
      await removeOffline(trackId);
      // Free the slot server-side too. Own-row delete is allowed by RLS
      // (migration 103); failing is harmless, the lease just expires.
      if (user) {
        await supabase.from('offline_leases')
          .delete().eq('user_id', user.id).eq('track_id', trackId);
      }
      await refresh();
      return true;
    } catch (err) {
      console.warn('[offline] remove failed', trackId, err?.message || err);
      return false;
    }
  }, [user, refresh]);

  const removeAll = useCallback(async () => {
    try {
      await clearOffline();
      if (user) await supabase.from('offline_leases').delete().eq('user_id', user.id);
      await refresh();
      return true;
    } catch (err) {
      console.warn('[offline] clear failed', err?.message || err);
      return false;
    }
  }, [user, refresh]);

  const savedIds = React.useMemo(
    () => new Set(items.filter(m => leaseState(m) !== 'expired').map(m => m.trackId)),
    [items]
  );

  const value = {
    items,
    loading,
    supported,
    usage,
    saving,
    error,
    errorCode,
    clearError: () => { setError(''); setErrorCode(''); },
    savedIds,
    isSaved:    id => savedIds.has(id),
    savingProgress: id => saving[id],
    save,
    cancelSave,
    remove,
    removeAll,
    refresh,
    leaseState,
    daysLeft,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

// Safe defaults if something renders outside the provider, matching the
// pattern useTier already uses. A missing provider must not crash a page , 
// it just means nothing is saved.
const FALLBACK = {
  items: [], loading: false, supported: false,
  usage: { count: 0, bytes: 0, quota: null },
  saving: {}, error: '', clearError: () => {},
  savedIds: new Set(), isSaved: () => false, savingProgress: () => undefined,
  save: async () => false, cancelSave: () => {},
  remove: async () => false, removeAll: async () => false,
  refresh: async () => {},
  leaseState: () => 'missing', daysLeft: () => null,
};

export function useOfflineLibrary() {
  return useContext(OfflineContext) || FALLBACK;
}